import React from "react";

export const DEPT_LABELS = {
  design: "Design",
  material: "Material",
  procurement: "Procurement",
  production: "Production",
  projects: "Projects",
  commercial: "Commercial",
};

export const STATUS_LABELS = {
  not_started: "Not started",
  in_progress: "In progress",
  done: "Done",
  blocked: "Blocked",
};

export function Card({ className = "", children, ...props }) {
  return (
    <div className={`card-surface ${className}`} {...props}>
      {children}
    </div>
  );
}

export function SectionTitle({ children, icon: Icon, hint, action }) {
  return (
    <div className="flex items-center justify-between mb-4">
      <div className="flex items-center gap-2.5">
        {Icon && <Icon className="text-gold" size={18} strokeWidth={1.6} />}
        <h2 className="font-heading text-xl tracking-tight text-foreground">{children}</h2>
        {hint && <span className="text-xs text-muted-foreground">{hint}</span>}
      </div>
      {action}
    </div>
  );
}

export function Pill({ children, tone = "muted", className = "", ...props }) {
  const tones = {
    muted: "bg-muted text-muted-foreground border-border",
    gold: "bg-gold/15 text-gold border-gold/30",
    critical: "bg-critical/15 text-critical border-critical/40",
    warning: "bg-warning/15 text-warning border-warning/40",
    success: "bg-success/20 text-success border-success/40",
    info: "bg-info/15 text-info border-info/40",
  };
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-[11px] font-medium leading-tight ${tones[tone]} ${className}`}
      {...props}
    >
      {children}
    </span>
  );
}

export function StatusBadge({ status, complete }) {
  const tone = complete ? "success" : status === "Not started" ? "muted" : "info";
  return <Pill tone={tone} data-testid="status-badge">{status || "—"}</Pill>;
}

export function DueBadge({ daysLeft, overdue }) {
  if (overdue) {
    return <Pill tone="critical" data-testid="due-badge">{Math.abs(daysLeft)}d overdue</Pill>;
  }
  const tone = daysLeft <= 2 ? "critical" : daysLeft <= 5 ? "warning" : "muted";
  return <Pill tone={tone} data-testid="due-badge">due in {daysLeft}d</Pill>;
}

export function ProjectBadge({ badge, compressed }) {
  if (compressed) return <Pill tone="gold" data-testid="badge-compressed">Compressed</Pill>;
  const map = {
    critical: { tone: "critical", label: "Critical" },
    at_risk: { tone: "warning", label: "At risk" },
    on_track: { tone: "success", label: "On track" },
  };
  const b = map[badge] || map.on_track;
  return <Pill tone={b.tone} data-testid={`badge-${badge}`}>{b.label}</Pill>;
}

export function aed(v) {
  if (v == null) return "—";
  return "AED " + Number(v).toLocaleString("en-AE", { maximumFractionDigits: 0 });
}

export function initials(name) {
  if (!name) return "—";
  return name.split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
}

export function EmptyState({ icon: Icon, title, subtitle }) {
  return (
    <div className="flex flex-col items-center justify-center py-14 text-center">
      {Icon && <Icon className="text-muted-foreground/50 mb-3" size={34} strokeWidth={1.3} />}
      <p className="text-foreground/80 font-medium">{title}</p>
      {subtitle && <p className="text-sm text-muted-foreground mt-1 max-w-sm">{subtitle}</p>}
    </div>
  );
}

export function Loader({ label = "Loading" }) {
  return (
    <div className="flex items-center gap-2 text-muted-foreground py-16 justify-center">
      <span className="w-4 h-4 rounded-full border-2 border-gold border-t-transparent animate-spin" />
      <span className="text-sm">{label}…</span>
    </div>
  );
}
